import { AdditionalConfig, DashCardDto } from './dto/dashboard.types.dto'
import {
  CardSize,
  CardType,
  ChartDataSource,
  ChartType,
  DateAxisType,
  DateRangeType,
  MetricDisplayType,
  MetricSplitSubsetMode,
  Quadrant,
  TaskDurationAggregation,
  TargetComparison,
  ValueAggregationType,
} from './enums'

export class AppMatCard implements DashCardDto {
  cols: number
  rows: number

  id: number
  size: CardSize
  title: string

  /** Chart vs Metric - stashed inside additionalConfig, defaults to Chart for legacy cards */
  cardType: CardType
  /** only set on `extraSmall` metrics */
  quadrant?: Quadrant
  /** groups `extraSmall` metrics sharing the same small cell slot */
  quadGroupId?: number

  ///  CHART - mapped to ChartConfig interface ///

  dataSourceId?: ChartDataSource

  /** a number field on object to chart on y axis.
        if null or DEFAULT_VALUE_FIELD,
        charts will use the number of matched objects */
  valueField: string
  valueAggregationTypeId: ValueAggregationType
  /** only applies with value aggregation type 'cumulative */
  startingValue: number

  /** field to group objects by on x axis */
  argumentField: string

  splitByField: string

  /** only show aggregation and splitting categories that exist in filtered data
   *  i.e. filter by date range and any custom filters before generating list of chart series + creating (non-date) aggregate groups
   *  doesn't apply to task-duration charts yet  */
  showFilteredCategoriesOnly: boolean

  dateRangeTypeId?: DateRangeType
  dateFilterField?: string
  dateFrom?: Date
  dateTo?: Date
  dateAxisTypeId?: DateAxisType
  includeCurrentPartialPeriod: boolean

  chartTypeId?: ChartType
  filters?: string
  chartColour?: string
  targetValue?: number

  /** used by some datasources */
  taskMasterId?: number
  taskMaster2Id?: number
  task1DateField?: string
  task2DateField?: string
  taskDurationAggregation: TaskDurationAggregation

  config?: AdditionalConfig

  /** how a metric card displays its value - defaults to Numeric for legacy cards */
  get metricDisplayType(): MetricDisplayType {
    return this.config?.metricDisplayType ?? MetricDisplayType.Numeric
  }

  get metricSplitSubsetMode(): MetricSplitSubsetMode {
    return this.config?.metricSplitSubsetMode ?? MetricSplitSubsetMode.Highest
  }

  get metricSplitDisplayCount(): number {
    return Math.max(1, Math.min(8, this.config?.metricSplitDisplayCount ?? 8))
  }

  /** target comparison defaults to the legacy higher-is-better behaviour */
  get targetComparison(): TargetComparison {
    return this.config?.targetComparison ?? TargetComparison.AtLeast
  }

  constructor(card: DashCardDto, cols: number, rows: number) {
    this.cols = cols
    this.rows = rows

    this.argumentField = card.argumentField
    this.chartColour = card.chartColour
    this.chartTypeId = card.chartTypeId
    this.dataSourceId = card.dataSourceId
    this.dateAxisTypeId = card.dateAxisTypeId
    this.dateFilterField = card.dateFilterField
    this.dateFrom = card.dateFrom
    this.dateRangeTypeId = card.dateRangeTypeId
    this.dateTo = card.dateTo
    this.filters = card.filters
    this.id = card.id
    this.showFilteredCategoriesOnly = card.showFilteredCategoriesOnly
    this.size = card.size
    this.splitByField = card.splitByField
    this.startingValue = card.startingValue
    this.targetValue = card.targetValue
    this.task1DateField = card.task1DateField
    this.task2DateField = card.task2DateField
    this.taskDurationAggregation = card.taskDurationAggregation
    this.taskMaster2Id = card.taskMaster2Id
    this.taskMasterId = card.taskMasterId
    this.title = card.title
    this.valueAggregationTypeId = card.valueAggregationTypeId
    this.valueField = card.valueField
    this.includeCurrentPartialPeriod = card.includeCurrentPartialPeriod

    this.config = JSON.parse(card.additionalConfig || '{}') as AdditionalConfig
    this.cardType = this.config.cardType ?? CardType.Chart
    this.quadrant = this.config.quadrant
    this.quadGroupId = this.config.quadGroupId
  }
}
